import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { modifyProviderDrawerSections } from '@common/constants';
import { useGetProviderLovsList, useGetTitlesList } from '@common/slices';
import { ModifyProviderLookups } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useDynamicFormDrawerStyles } from '@components/dynamic-form/style';
import Loader from '@components/loader/Loader';
import { RootState } from '@core/redux/store';
import { IGeneralDetails } from '@features/salesforce/providers';
import { useUpdateProvider } from '@features/salesforce/providers/providers.slice';
import { enumToList, lookupToList } from '@utils';

import { useFormik } from 'formik';
import * as Yup from 'yup';

interface Props {
  onSuccess: () => void;
  data?: IGeneralDetails;
}

const ModifyProviderDrawer: React.FC<Props> = ({ onSuccess, data }: Props) => {
  const buttonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );

  const { classes } = useDynamicFormDrawerStyles();

  const { data: titles } = useGetTitlesList();
  const { data: providerLovs } = useGetProviderLovsList();
  const [updateProvider] = useUpdateProvider(data.id);

  const [lookups, setLookups] = useState<ModifyProviderLookups>(null);

  const modifyProviderFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: data?.typeCode ?? '',
      isCompany: data?.isCompany ?? false,
      isLocal: data?.isLocal ?? false,
      contractStartDate: data?.contractStartDate ?? '',
      contractEndDate: data?.contractEndDate ?? '',
      title: data?.title ?? '',
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      middleName: data?.middleName ?? '',
      dateOfBirth: data?.dateOfBirth ?? '',
      email: data?.email ?? '',
      phoneType: data?.phoneType?.toUpperCase() ?? '',
      phoneNumber: data?.phoneNumber ?? '',
      isTaxable: data?.isTaxable ?? false,
      auxiliary: data?.auxiliary ?? '',
      taxAuxiliary: data?.taxAuxiliary ?? ''
    },
    validationSchema: Yup.object({
      isCompany: Yup.boolean().notRequired(),
      isLocal: Yup.boolean().notRequired(),
      contractStartDate: Yup.date().required('Required'),
      auxiliary: Yup.string()
        .required('Required')
        .length(8, 'Auxiliary should be 8 characters'),
      taxAuxiliary: Yup.string()
        .required('Required')
        .length(8, 'Tax Auxiliary should be 8 characters')
    }),
    onSubmit: async (values) => {
      if (!data?.id) {
        return;
      }
      await updateProvider({
        providerId: data.id,
        updateProviderInput: {
          contractStartDate: values.contractStartDate,
          isLocal: values.isLocal,
          isCompany: values.isCompany,
          auxiliary: values.auxiliary,
          taxAuxiliary: values.taxAuxiliary
        }
      }).then(() => {
        setTimeout(onSuccess, 500);
      });
    }
  });

  const handleChange = async (
    fieldName: string,
    value: string | number | boolean
  ) => {
    modifyProviderFormik.setFieldValue(fieldName, value);
    modifyProviderFormik.setFieldTouched(fieldName, true);
  };

  useMemo(async () => {
    if (titles && providerLovs)
      setLookups({
        titles: lookupToList(titles?.core?.getTitles),
        phoneType: enumToList(providerLovs?.phoneType?.enumValues),
        providerTypes: lookupToList(providerLovs?.salesforce?.getProviderTypes),
        status: enumToList(providerLovs?.providerStatusList?.enumValues)
      });
  }, [titles, providerLovs]);

  return (
    <>
      {!lookups ? (
        <div className={classes.loaderContainer}>
          <Loader />
        </div>
      ) : (
        // Catch any bubbling submit from inside the drawer
        <div>
          <DynamicFormWidget
            onChange={handleChange}
            sections={modifyProviderDrawerSections(
              modifyProviderFormik,
              lookups
            )}
            disableForm={false}
            onSubmit={() => modifyProviderFormik.handleSubmit()}
            submitButtonState={buttonState}
          />
        </div>
      )}
    </>
  );
};

export { ModifyProviderDrawer };
